/**
 * # dxfBridge.annotativeExport (S-INSERT-ANNOTATIVE, audit §12.1)
 *
 * Re-synthesize the OBJECTS-section annotative scale graph from the typed
 * DrawingDocument on export. The bridge IMPORT resolves each annotative
 * entity's per-scale context-data into `entity.annotativeContexts` (and lifts
 * the AcDbScale catalogue into `header.annotationScales`), but the bridge
 * EXPORT previously built a fresh OBJECTS section that omitted the
 * `annotScaleContexts` / `acadScales` / `acadScaleListDict` records entirely —
 * so a resolved annotative INSERT (and TEXT / MTEXT / HATCH) lost its contexts
 * on import → export → re-import.
 *
 * The raw dxf-io level already round-trips this graph (the writer auto-emits
 * `objectsSection.annotScaleContexts` / `acadScales` / `acadScaleListDict`
 * when present — see writer.ts:2125+). This helper rebuilds those three typed
 * structures from the model so the writer can emit them; the inverse path
 * (`resolveAnnotativeContexts`) then re-attaches the contexts on re-import.
 *
 * ## Anchor strategy (matches `resolveAnnotativeContexts`)
 *
 * The resolver matches a context record to its owning entity by
 * `ctxRecord.ownerHandle === entity.common.extensionDictionaryHandle`
 * (handleResolution.ts direct-anchor path — no DICTIONARY object required).
 * On export we therefore emit each context record with
 * `ownerHandle = <the entity's EMITTED ext-dict handle>` — the SAME 360 the
 * writer advertises on the entity. For an INSERT carrying a per-INSERT
 * representation override, `toDxfEntity` advertises
 * `representationOverrides.representationDataHandle` as that 360 (it overrides
 * the common ext-dict), and an INSERT's ext-dict 360 is ONE dictionary that can
 * own BOTH the representation entry AND the annotative context-data — so the
 * context owner anchors to the representation handle in that case (F1, Codex
 * #252 P2). Otherwise the owner is the common `extensionDictionaryHandle`
 * (preserved verbatim on the model since import). When an annotative entity
 * carries contexts but advertises NO ext-dict handle at all (pure-model
 * authored / synthetic, no representation override), we mint a deterministic hex
 * handle and stamp it onto the entity so the FK chain re-resolves.
 *
 * ## Scale catalogue
 *
 * Each unique `context.scaleName` becomes one `AcDbScale` catalogue entry +
 * one `ACAD_SCALELIST` dictionary pointer. `scaleFactor` is preserved from the
 * resolved context (it equals the AcDbScale paperUnits/drawingUnits ratio);
 * paperUnits/drawingUnits are reconstructed as `scaleFactor` / `1` which the
 * parser collapses back to the same ratio on re-import.
 *
 * Reference: DXF AcDbObjectContextData class hierarchy + ezdxf annotative
 * scale infrastructure.
 */

import type {
  AcDbScale,
  AcadScaleListDictEntry,
  AnnotScaleContext as DxfAnnotScaleContext,
  DxfObjectsSection,
} from "@dxf-ai-editor/dxf-io";

import type { AnnotScaleContext as ModelAnnotScaleContext } from "./annotative";
import {
  isLayoutBlockName,
  isPaperSpaceBlockName,
} from "./blockNaming";
import {
  hasPairedPaperSpace,
  isFirstPaperSpaceBlockName,
  type PaperSpaceLayoutPlan,
} from "./dxfBridge.paperSpaceLayout";
import type { AttdefEntity, DrawingDocument, DrawingEntity } from "./model";

/**
 * Entity types whose `annotativeContexts` field uses the SHARED resolver union
 * (`annotScaleContextSchema`). These are exactly the types the import resolver
 * populates (dxfBridge.import.ts second pass: Text / MText / Hatch / Block /
 * Attdef).
 *
 * ATTDEF is a BLOCK-DEFINITION child only (`blockChildEntitySchema`, NOT the
 * top-level `drawingEntitySchema`), so its `"Attdef"` literal is not a member of
 * `DrawingEntity["type"]` — the Set is typed as that union PLUS `"Attdef"` and
 * `AnnotativeBearingEntity` extracts over `DrawingEntity | AttdefEntity`.
 * ATTDEF carries the IDENTICAL `annotScaleContextSchema` union as
 * TEXT/MTEXT/HATCH/INSERT (its context kind is `attribute` / `mtext_attribute`,
 * both handled by {@link subclassForKind}), and the import resolver
 * (dxfBridge.import.ts admits `child.type === "Attdef"`) now populates a block
 * ATTDEF's contexts — so the export graph MUST re-emit them, else an
 * import → export → reimport of an annotative block attribute drops the child's
 * per-scale contexts back to `[]` and the S10d renderer loses its per-scale
 * placement/visibility after reopen (@codex round-23, dxfBridge.import.ts:1015).
 *
 * DIMENSION and MULTILEADER are DELIBERATELY EXCLUDED — DIMENSION's
 * `annotativeContexts` still uses the LEGACY `annotativeContextSchema` shape
 * (structurally incompatible with the resolver union) and MLEADER receives its
 * contexts inline via a dedicated `mleaderContextDataSchema`, not the OBJECTS
 * second-pass resolver. Re-synthesizing OBJECTS records for them here would
 * emit schema-invalid context bodies (and crash the dxf-io writer's
 * subclass dispatch). They are tracked separately (S-DIMENSION-EXTRAS /
 * S-MLEADER-ANNOTATIVE per audit §12.1). Mirrors the import-side exclusion.
 */
const ANNOTATIVE_RESOLVER_TYPES = new Set<DrawingEntity["type"] | "Attdef">([
  "Text",
  "MText",
  "Hatch",
  "Block",
  "Attdef",
]);

/** Entity types whose schema carries `annotativeContexts` (resolver output). */
type AnnotativeBearingEntity = Extract<
  DrawingEntity | AttdefEntity,
  { annotativeContexts: ModelAnnotScaleContext[] }
>;

function hasAnnotativeContexts(
  entity: DrawingEntity | AttdefEntity,
): entity is AnnotativeBearingEntity {
  return (
    ANNOTATIVE_RESOLVER_TYPES.has(entity.type) &&
    Array.isArray(
      (entity as { annotativeContexts?: unknown }).annotativeContexts,
    ) &&
    (entity as { annotativeContexts: unknown[] }).annotativeContexts.length > 0
  );
}

/**
 * Concrete subclass marker for an annotative context kind. Mirrors the
 * dxf-io writer's `resolveSubclass` fallback so the writer emits the canonical
 * registered DXF class name and the parser dispatches back to the same kind.
 */
function subclassForKind(kind: ModelAnnotScaleContext["kind"]): string {
  switch (kind) {
    case "text":
      return "AcDbTextObjectContextData";
    case "mtext":
      return "AcDbMTextObjectContextData";
    case "mtext_attribute":
      return "AcDbMTextAttributeObjectContextData";
    case "attribute":
    case "blockref":
      return "AcDbBlkRefObjectContextData";
    case "hatch":
      return "AcDbHatchScaleContextData";
    case "dimension":
      return "AcDbDimensionObjectContextData";
    case "mleader":
      return "AcDbMLeaderObjectContextData";
    case "leader":
      return "AcDbLeaderObjectContextData";
    case "tolerance":
      return "AcDbFcfObjectContextData";
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

/**
 * The DXF group codes under which a VERBATIM handle string can appear inside a
 * raw group array (`groups`, `rawTableGroups`, `rawObjectGroups`,
 * `rawBlockGroups`, XData `extraGroups`) — i.e. carrying NO `*Handle` key, so the
 * structural `key === "handle" || key.endsWith("Handle")` walk would miss them.
 * The harvested own-handle set is EXACTLY the two own-handle codes plus the one
 * xdata reference kept for collision-safety:
 *   - 5    — the record's GENERAL own handle.
 *   - 105  — the DIMSTYLE record's own handle (the SOLE non-5 own-handle code per
 *            Autodesk DXF ref + ezdxf; a DIMSTYLE table entry emits its own handle
 *            under 105, never 5). Omitting it under-seeds `$HANDSEED` below a live
 *            DIMSTYLE handle → a later edit mints a duplicate.
 *   - 1005 — an XData handle REFERENCE (not an own-handle), kept so a minted
 *            handle never aliases an existing xdata reference (collision-safety).
 * Reference codes 320/330/340/350/360 are NOT listed — they are caught by the
 * structural `key.endsWith("Handle")` walk in {@link collectHandlesDeep}.
 *
 * SINGLE SOURCE OF TRUTH — `collectSkeletonHandles` (dxfBridge.conformantSkeleton)
 * imports this set so the skeleton minter seed and the final `$HANDSEED` census
 * can never diverge on which group codes carry handles.
 */
export const OWN_HANDLE_GROUP_CODES = new Set<number>([5, 105, 1005]);

/**
 * A DxfGroup-shaped leaf carrying a verbatim DXF handle: an own-handle code
 * (5 general, 105 DIMSTYLE) or 1005 (an XData handle reference). These live
 * VERBATIM inside raw group arrays (`groups`, `rawTableGroups`,
 * `rawObjectGroups`, `rawBlockGroups`, XData `extraGroups`) under no `*Handle`
 * key, so the structural `key === "handle" || key.endsWith("Handle")` walk below
 * would miss them without this leaf check (see {@link OWN_HANDLE_GROUP_CODES}).
 */
function harvestRawHandleGroup(item: unknown, out: Set<string>): void {
  if (item == null || typeof item !== "object") return;
  const code = (item as { code?: unknown }).code;
  if (typeof code !== "number" || !OWN_HANDLE_GROUP_CODES.has(code)) return;
  const v = (item as { value?: unknown }).value;
  if (typeof v === "string" && v.length > 0) out.add(v.toLowerCase());
}

/**
 * SHARED handle-field-shape classifiers (SINGLE SOURCE OF TRUTH) — imported by
 * BOTH the census/seed walk ({@link collectHandlesDeep}) AND the conformant-
 * skeleton structural rewrite (`rewriteEntityHandlesGeneric` in
 * dxfBridge.conformantSkeleton.handles) so the two sites cannot DRIFT on what
 * counts as a single-handle field vs a handle-array field. Drift here was the
 * Axis-A defect: the seed walk only harvested STRING handles, so a `string[]`
 * handle-array member (`sourceBoundaryHandles` / `frozenLayerHandles` /
 * `reactorHandles` / `dependencyHandles`) was never seeded → a from-scratch
 * minter could mint a handle that ALIASES a copied array member (duplicate
 * group-5 → strict ezdxf reject).
 */
/** True for a key that names a SINGLE-handle field (`handle` or `*Handle`). */
export function isSingleHandleKey(key: string): boolean {
  return key === "handle" || key.endsWith("Handle");
}

/** True for a key that names a handle-ARRAY field (`*Handles`). */
export function isHandleArrayKey(key: string): boolean {
  return key.endsWith("Handles");
}

/**
 * Deep-walk an arbitrary object graph collecting EVERY handle it carries
 * (lowercased) into `out`:
 *   - any string value under a key named `handle` or ending in `Handle`
 *     (the record's own group-5 handle + every soft/hard-pointer ref), AND
 *   - any verbatim `{code: 5 | 105 | 1005, value}` DxfGroup leaf inside ANY array
 *     (covers `groups` / `rawTableGroups` / `rawObjectGroups` / `rawBlockGroups`
 *     / XData `extraGroups`, where handles live under no `*Handle` key — 5 general
 *     own-handle, 105 DIMSTYLE own-handle, 1005 xdata reference; see
 *     {@link OWN_HANDLE_GROUP_CODES}).
 *
 * The walk is STRUCTURAL (no hand-maintained field list) so it stays correct as
 * new handle-bearing fields are added to the model. Shared by the export
 * MLEADER/annotative minter seeding (over assembled `DxfSection[]`) and the
 * conformant-skeleton entity re-minter seeding (over a `DrawingDocument` that
 * carries COPIED source handles, e.g. a WBLOCK-from-imported wrapper) so a
 * minted handle never aliases an existing copied/structural handle.
 */
export function collectHandlesDeep(node: unknown, out: Set<string>): void {
  if (node == null) return;
  if (Array.isArray(node)) {
    for (const item of node) {
      harvestRawHandleGroup(item, out);
      collectHandlesDeep(item, out);
    }
    return;
  }
  if (typeof node !== "object") return;
  const record = node as Record<string, unknown>;
  for (const [key, value] of Object.entries(record)) {
    if (
      typeof value === "string" &&
      value.length > 0 &&
      isSingleHandleKey(key)
    ) {
      out.add(value.toLowerCase());
    } else if (
      Array.isArray(value) &&
      isHandleArrayKey(key) &&
      value.every((h) => typeof h === "string")
    ) {
      // A STRING[] handle-array field (`sourceBoundaryHandles` /
      // `frozenLayerHandles` / `reactorHandles` / `dependencyHandles`). Seed each
      // non-empty member LOWERCASED — MIRRORS the scalar branch above and the
      // string-array branch of `rewriteEntityHandlesGeneric` (the rewrite that
      // could re-point one of these refs at a reminted entity). Without this the
      // minter is blind to a copied array member and may ALIAS it (duplicate
      // group-5 → strict ezdxf reject). The `every(isString)` guard EXCLUDES the
      // `{code, handle}[]` carrier (`layerOverrideHandles`, also `*Handles`-named)
      // and the `{code, value}[]` raw-group arrays — those object entries are
      // harvested by `harvestRawHandleGroup` (1005 leaf) + the recursive descent
      // below, so they are not double-handled here.
      for (const h of value as string[]) {
        if (h.length > 0) out.add(h.toLowerCase());
      }
    } else if (value != null && typeof value === "object") {
      collectHandlesDeep(value, out);
    }
  }
}

/**
 * SL-5 F3 (@codex round-6) — the group codes under which a record carries its OWN
 * (group-5-class) handle inside a raw group array: 5 (general own handle) and 105
 * (DIMSTYLE own handle). EXCLUDES 1005 (an xdata REFERENCE — in
 * {@link OWN_HANDLE_GROUP_CODES} only for minter collision-safety) because a reuse-
 * collision guard must compare a candidate handle against genuine OWN handles, not
 * a mere pointer to one.
 */
const RECORD_OWN_HANDLE_GROUP_CODES = new Set<number>([5, 105]);

/**
 * SL-5 F3 (@codex round-6) — deep-walk collecting ONLY a graph's OWN
 * (group-5-class) handles: the string under a key named EXACTLY `handle`, plus raw
 * `{code:5|105}` leaves. REFERENCE handles (`*Handle` keys such as `ownerHandle` /
 * `leaderStyleHandle`, verbatim 320/330/340/350/360 and 1005 xdata, `*Handles`
 * arrays) are EXCLUDED so a record's own handle is distinguished from a pointer TO
 * it. Counterpart to {@link collectHandlesDeep} (which collects EVERY handle, own
 * AND reference).
 *
 * Used by the MLEADERSTYLE source-handle reuse guard
 * (`buildReferencedMLeaderStyleObjects`): a reused imported source handle may
 * legitimately alias its OWN style's REFERENCES (the ACAD_MLEADERSTYLE dict 350
 * child + the entity body 340) — those are self-references, not collisions — but
 * must NEVER alias a DIFFERENT record's own group-5 handle (a merge/insert
 * overlapping-handle-space duplicate → two records share a group-5 → ambiguous
 * 340). Because the source handle appears document-wide ONLY as those references
 * (the source MLEADERSTYLE record is consumed to the typed model, never a raw
 * group-5), an own-only walk never blocks the legitimate F6 byte-stable reuse.
 */
export function collectOwnHandlesDeep(node: unknown, out: Set<string>): void {
  if (node == null) return;
  if (Array.isArray(node)) {
    for (const item of node) {
      if (item != null && typeof item === "object") {
        const code = (item as { code?: unknown }).code;
        if (
          typeof code === "number" &&
          RECORD_OWN_HANDLE_GROUP_CODES.has(code)
        ) {
          const v = (item as { value?: unknown }).value;
          if (typeof v === "string" && v.length > 0) out.add(v.toLowerCase());
        }
      }
      collectOwnHandlesDeep(item, out);
    }
    return;
  }
  if (typeof node !== "object") return;
  for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
    if (typeof value === "string" && value.length > 0 && key === "handle") {
      out.add(value.toLowerCase());
    } else if (value != null && typeof value === "object") {
      collectOwnHandlesDeep(value, out);
    }
  }
}

/**
 * Deterministic hex-handle minter, collision-checked against the document-wide
 * handle population. By default starts above the entity/block/OBJECTS magnitude
 * (0x200000) so it never collides with the MLEADER text-style minter (0x100000+).
 *
 * `startSeq` lets a caller mint from a different high base (e.g. the conformant
 * skeleton entity re-minter uses 0x100000+, BELOW the annotative range, and the
 * annotative/MLEADER minters collision-check against the document-wide emitted
 * handle population that already includes those reminted handles). The minter
 * always skips any candidate already in `seedHandles`, so distinct ranges plus
 * seed-checking guarantee uniqueness across all minters in one export.
 */
/**
 * A handle minter callable that ALSO exposes its live used-handle population so a
 * caller minting handles OUT-OF-BAND (an already-hex value kept verbatim) can:
 *   - `has(handle)` — test whether a candidate already collides with a minted /
 *     seeded / reserved handle (so a kept-verbatim handle that COLLIDES is
 *     reminted instead of emitted as a duplicate group-5 — BUG 3);
 *   - `reserve(handle)` — register a kept-verbatim handle so a LATER mint / keep
 *     never re-issues it.
 * Both are case-insensitive (DXF handles are). `reserve` returns `true` when the
 * handle was free (now reserved) and `false` when it already collided.
 */
export interface HandleMinter {
  (): string;
  /** Case-insensitive: is `handle` already minted / seeded / reserved? */
  has(handle: string): boolean;
  /** Reserve a kept-verbatim handle; returns false if it already collided. */
  reserve(handle: string): boolean;
}

export function makeHandleMinter(
  seedHandles: ReadonlySet<string>,
  startSeq = 0x200000,
): HandleMinter {
  const used = new Set<string>();
  for (const h of seedHandles) used.add(h.toLowerCase());
  let seq = startSeq;
  const mint = (): string => {
    let candidate: string;
    do {
      seq += 1;
      candidate = seq.toString(16).toUpperCase();
    } while (used.has(candidate.toLowerCase()));
    used.add(candidate.toLowerCase());
    return candidate;
  };
  const minter = mint as HandleMinter;
  minter.has = (handle: string): boolean =>
    handle.length > 0 && used.has(handle.toLowerCase());
  minter.reserve = (handle: string): boolean => {
    if (handle.length === 0) return false;
    const lower = handle.toLowerCase();
    if (used.has(lower)) return false;
    used.add(lower);
    return true;
  };
  return minter;
}

/**
 * Result of {@link buildAnnotativeObjectsGraph} — the three typed structures
 * the dxf-io writer auto-emits, plus the set of entities whose ext-dict handle
 * was minted (so the caller can stamp them onto the emitted entity records).
 */
export interface AnnotativeObjectsGraph {
  acadScales: AcDbScale[];
  acadScaleListDict?: AcadScaleListDictEntry;
  annotScaleContexts: NonNullable<DxfObjectsSection["annotScaleContexts"]>;
  /** entity.id → minted extension-dictionary handle (when absent on import). */
  mintedExtDictHandles: Map<string, string>;
  /**
   * F3 — handle of a PRESERVED `ACAD_SCALELIST` DICTIONARY (in
   * `document.rawObjectGroups`) whose handle the synthesized
   * `acadScaleListDict` REUSES. The caller must STRIP that stale preserved
   * container record from `rawObjectGroups` (via {@link stripPreservedScaleListDict})
   * before emitting the synthesized one — otherwise the output carries TWO
   * `ACAD_SCALELIST` dictionaries. Undefined when no preserved SCALELIST existed
   * (a fresh handle was minted) or no annotative graph is emitted.
   */
  reusedScaleListHandle?: string;
}

/**
 * Harvest EVERY handle {@link buildAnnotativeObjectsGraph} minted (or reused)
 * across ALL FOUR of its output channels — `acadScales[].handle`, the
 * per-scale `annotScaleContexts[]` own + reference handles (`handle` /
 * `ownerHandle` / `scaleHandle`), the `acadScaleListDict` container's own
 * handle, and `mintedExtDictHandles` — via the SAME structural
 * {@link collectHandlesDeep} walk the rest of the export pipeline already
 * uses for collision-safe minter seeding. `mintedExtDictHandles` is a `Map`
 * (NOT walked structurally by `collectHandlesDeep`, which only descends plain
 * objects/arrays via `Object.entries`), so its values are folded in
 * explicitly.
 *
 * @codex P1 (N7 PR #556 round 3) — a LATER minting pass in the SAME export
 * (e.g. the VPCLIP-BOUNDARY-EXPORT viewport-clip synthesis) MUST seed its
 * minter with this FULL set, not just `mintedExtDictHandles`.
 * `buildAnnotativeObjectsGraph`'s own internal minter is entirely PRIVATE
 * (fresh `makeHandleMinter` per call, default floor `0x200000`) — seeding a
 * later minter with only the ext-dict handles left every `AcDbScale` /
 * context / SCALELIST handle invisible to it. Once the annotative graph mints
 * more handles than the gap between its own floor and the later minter's
 * floor, the two independent minters can pick the SAME candidate — a
 * duplicate group-5 handle (strict `ezdxf` / AutoCAD readfile rejects it).
 */
export function collectAnnotativeGraphMintedHandles(
  graph: Pick<
    AnnotativeObjectsGraph,
    "acadScales" | "annotScaleContexts" | "acadScaleListDict" | "mintedExtDictHandles"
  >,
): Set<string> {
  const out = new Set<string>();
  collectHandlesDeep(graph.acadScales, out);
  collectHandlesDeep(graph.annotScaleContexts, out);
  if (graph.acadScaleListDict) collectHandlesDeep(graph.acadScaleListDict, out);
  for (const h of graph.mintedExtDictHandles.values()) {
    if (h.length > 0) out.add(h.toLowerCase());
  }
  return out;
}

/**
 * Locate the handle of a PRESERVED `ACAD_SCALELIST` DICTIONARY in a document's
 * verbatim `rawObjectGroups`.
 *
 * On import the bridge re-injects every parsed OBJECTS-section DICTIONARY record
 * into `document.rawObjectGroups` verbatim (dxfBridge.import.ts) — INCLUDING the
 * `ACAD_SCALELIST` container DICTIONARY. The AcDbScale catalogue entries
 * themselves are pulled OUT into the typed `annotationScales` model (and are NOT
 * re-injected raw), but the SCALELIST DICTIONARY *container* survives raw. The
 * bridge export then re-synthesizes a FRESH `acadScaleListDict` from the typed
 * graph — so without this detection the output would carry TWO `ACAD_SCALELIST`
 * dictionaries (the preserved raw one + the synthesized one), each pointing at a
 * DIFFERENT set of AcDbScale handles. (The writer's de-dup guard only inspects
 * `section.dictionaries`, which the bridge export always emits as `[]` — it never
 * sees the preserved raw copy.)
 *
 * The named-object ROOT DICTIONARY carries a `3/ACAD_SCALELIST` entry followed by
 * a `350`/`360` handle pointing at the SCALELIST DICTIONARY. We return that
 * handle so the caller can (a) STRIP the now-stale preserved SCALELIST container
 * from `rawObjectGroups` and (b) REUSE the handle for the synthesized dict — so
 * the root dictionary's existing `350` pointer still resolves to exactly ONE
 * SCALELIST container whose entries point at the (only) emitted AcDbScale records
 * (the synthesized ones). Returns `undefined` when no preserved SCALELIST exists
 * (e.g. a pure-model document, or a DXF authored without an ACAD_SCALELIST).
 */
export function findPreservedScaleListHandle(
  rawObjectGroups: ReadonlyArray<{ code: number; value: string }> | undefined,
): string | undefined {
  if (!rawObjectGroups) return undefined;
  for (let i = 0; i < rawObjectGroups.length; i++) {
    const g = rawObjectGroups[i]!;
    if (g.code === 3 && g.value.trim() === "ACAD_SCALELIST") {
      // The handle is the next 350 (soft) / 360 (hard) owner reference.
      for (let j = i + 1; j < rawObjectGroups.length; j++) {
        const gj = rawObjectGroups[j]!;
        if (gj.code === 350 || gj.code === 360) return gj.value.trim();
        // Stop at the next entry name (another 3/) or a record boundary (0/).
        if (gj.code === 3 || gj.code === 0) break;
      }
    }
  }
  return undefined;
}

/**
 * Strip the PRESERVED `ACAD_SCALELIST` DICTIONARY *container record* (handle ===
 * {@link scaleListHandle}) from a verbatim `rawObjectGroups` list. The record is
 * a `0/DICTIONARY` (or `0/ACDBDICTIONARYWDFLT`) whose own `5/<handle>` equals the
 * SCALELIST handle; we drop from that opening `0/` up to (but not including) the
 * NEXT `0/` record boundary. Every OTHER raw object group — including the ROOT
 * named-object DICTIONARY's `3/ACAD_SCALELIST` + `350/<handle>` pointer — is kept
 * verbatim so the root pointer still resolves to the REUSED synthesized dict.
 * Pure: returns a new array, never mutates the input.
 */
export function stripPreservedScaleListDict(
  rawObjectGroups: ReadonlyArray<{ code: number; value: string }>,
  scaleListHandle: string,
): Array<{ code: number; value: string }> {
  const target = scaleListHandle.toLowerCase();
  const out: Array<{ code: number; value: string }> = [];
  let i = 0;
  const n = rawObjectGroups.length;
  while (i < n) {
    const g = rawObjectGroups[i]!;
    const isDictOpener =
      g.code === 0 &&
      (g.value === "DICTIONARY" || g.value === "ACDBDICTIONARYWDFLT");
    if (isDictOpener) {
      // Peek the record's own handle (the first 5/ before the next 0/).
      let handle: string | undefined;
      for (let j = i + 1; j < n; j++) {
        const gj = rawObjectGroups[j]!;
        if (gj.code === 0) break;
        if (gj.code === 5) {
          handle = gj.value.trim();
          break;
        }
      }
      if (handle && handle.toLowerCase() === target) {
        // Skip this whole DICTIONARY record (up to the next 0/ boundary).
        let k = i + 1;
        while (k < n && rawObjectGroups[k]!.code !== 0) k++;
        i = k;
        continue;
      }
    }
    out.push(g);
    i++;
  }
  return out;
}

/**
 * SL-16 P3 — locate the OWNER handle for the synthesized `ACAD_SCALELIST`
 * DICTIONARY: the root NamedObjects DICTIONARY (the record that carries the
 * `3/ACAD_SCALELIST` entry — it also owns ACAD_GROUP / ACAD_LAYOUT / …). Its own
 * `5/<handle>` is the value AutoCAD/R2018 writes as the SCALELIST's `330` owner.
 *
 * The root dict is present in `document.rawObjectGroups` on BOTH export paths —
 * the imported round-trip (the real named-object dict, owner `0`) AND the
 * from-scratch conformant skeleton (which back-fills the same boilerplate root
 * dict) — so this single scan resolves the owner for both without minting a
 * dangling handle. Returns `undefined` when no `3/ACAD_SCALELIST` pointer exists
 * (a document that never carried a SCALELIST); the caller then leaves the synth
 * dict owner-less rather than inventing one.
 */
export function findNamedObjectsRootHandle(
  rawObjectGroups: ReadonlyArray<{ code: number; value: string }> | undefined,
): string | undefined {
  if (!rawObjectGroups) return undefined;
  let currentHandle: string | undefined;
  let isDict = false;
  for (const g of rawObjectGroups) {
    if (g.code === 0) {
      isDict = g.value === "DICTIONARY" || g.value === "ACDBDICTIONARYWDFLT";
      currentHandle = undefined;
      continue;
    }
    if (!isDict) continue;
    // The record's OWN handle is the first `5/` after its opening `0/DICTIONARY`
    // (102-block members carry 360/330, never 5, so this is unambiguous).
    if (g.code === 5 && currentHandle === undefined) {
      currentHandle = g.value.trim();
      continue;
    }
    if (g.code === 3 && g.value.trim() === "ACAD_SCALELIST") {
      return currentHandle;
    }
  }
  return undefined;
}

/**
 * SL-16 P3 — source-preserve the OWNER (`330`) + persistent-reactor block
 * (`102 {ACAD_REACTORS … 102 }`) off a PRESERVED `ACAD_SCALELIST` DICTIONARY
 * container in `rawObjectGroups`, so the re-synthesized dict (which REPLACES the
 * stripped preserved container) re-emits them verbatim. The container is the
 * `0/DICTIONARY` record whose own `5/<handle>` equals {@link scaleListHandle}.
 * A `330` INSIDE the `102 {ACAD_REACTORS` block is a reactor reference; the
 * `330` OUTSIDE any `102` block is the record's own owner. Returns an empty
 * object when no such container exists (fresh-synth path — caller falls back to
 * {@link findNamedObjectsRootHandle}).
 */
export function extractPreservedScaleListDictMeta(
  rawObjectGroups: ReadonlyArray<{ code: number; value: string }> | undefined,
  scaleListHandle: string,
): { ownerHandle?: string; reactorHandles?: string[] } {
  if (!rawObjectGroups) return {};
  const target = scaleListHandle.toLowerCase();
  const n = rawObjectGroups.length;
  for (let i = 0; i < n; i++) {
    const g = rawObjectGroups[i]!;
    const isDictOpener =
      g.code === 0 &&
      (g.value === "DICTIONARY" || g.value === "ACDBDICTIONARYWDFLT");
    if (!isDictOpener) continue;
    // Peek the record's own handle (first 5/ before the next 0/).
    let handle: string | undefined;
    let end = n;
    for (let j = i + 1; j < n; j++) {
      if (rawObjectGroups[j]!.code === 0) {
        end = j;
        break;
      }
      if (rawObjectGroups[j]!.code === 5 && handle === undefined) {
        handle = rawObjectGroups[j]!.value.trim();
      }
    }
    if (!handle || handle.toLowerCase() !== target) {
      i = end - 1;
      continue;
    }
    // Found the container — walk its body [i+1, end) extracting owner + reactors.
    let controlBlock: string | null = null;
    let ownerHandle: string | undefined;
    const reactorHandles: string[] = [];
    for (let j = i + 1; j < end; j++) {
      const gj = rawObjectGroups[j]!;
      if (gj.code === 102) {
        const v = gj.value.trim();
        if (v === "}") controlBlock = null;
        else if (v.startsWith("{")) controlBlock = v.slice(1);
        continue;
      }
      if (gj.code === 330) {
        if (controlBlock === "ACAD_REACTORS") {
          const h = gj.value.trim();
          if (h.length > 0) reactorHandles.push(h);
        } else if (controlBlock === null && ownerHandle === undefined) {
          ownerHandle = gj.value.trim();
        }
      }
    }
    const meta: { ownerHandle?: string; reactorHandles?: string[] } = {};
    if (ownerHandle) meta.ownerHandle = ownerHandle;
    if (reactorHandles.length > 0) meta.reactorHandles = reactorHandles;
    return meta;
  }
  return {};
}

/**
 * F5' — true when a LAYOUT block's SUPPORTED children are emitted via a SPACE
 * (not via the block def), so the annotative graph must NOT also scan the block
 * def (else the child is double-counted). Mirrors the #314 relocation matrix:
 *   - `*Model_Space`                → model content canonical in ENTITIES. TRUE.
 *   - first/active `*Paper_Space`    → first-paper content canonical in ENTITIES
 *     (case-insensitive — F4'). TRUE.
 *   - PAIRED extra `*Paper_Space<N>` → emitted via the paired SPACE copy. TRUE.
 *   - UNPAIRED suffixed `*Paper_Space<N>` → emitted from the BLOCK def (no paired
 *     space — F3'). FALSE: scan it here so its annotative children are counted.
 * The caller guards on `isLayoutBlockName(name)` first, so a non-layout (regular
 * user) block never reaches here.
 */
function isLayoutBlockEmittedViaSpace(
  name: string,
  paperPlan: PaperSpaceLayoutPlan | undefined,
): boolean {
  // `*Model_Space` (not a paper-space name) → always emitted via the model space.
  if (!isPaperSpaceBlockName(name)) return true;
  // Un-suffixed first/active paper (case-insensitive) → emitted via its space.
  if (isFirstPaperSpaceBlockName(name)) return true;
  // Suffixed `*Paper_Space<N>`: emitted via the paired space ONLY when the plan
  // resolved it to a space; an UNPAIRED suffixed tab is emitted from the block def.
  // F2'' (round-3) — CASE-INSENSITIVE: the plan keys `paperSpaceByBlockName` by
  // the BLOCK_RECORD name spelling while this is invoked with the BLOCK-DEFINITION
  // `name`; a record-vs-block casing mismatch must still pair (consistent with the
  // BLOCKS-section rebuild) so the annotative graph does not double-scan a tab
  // whose children are emitted via its paired space.
  return paperPlan
    ? hasPairedPaperSpace(paperPlan.paperSpaceByBlockName, name)
    : false;
}

/**
 * Walk every annotative-bearing entity (top-level + block children) and
 * re-synthesize the OBJECTS-section annotative graph. Returns an empty graph
 * (no scales / no contexts) when the document has no annotative entities.
 *
 * `entitiesSnapshot` (F2) — when the export orchestrator routes the document
 * through the compact-cache read-through wrapper, the ENTITIES serializer
 * iterates `entitiesSnapshot.get(space.id) ?? space.entities`. This builder MUST
 * scan the SAME per-space source: an annotative entity present ONLY in the
 * snapshot (stale/empty `space.entities`) would otherwise be written as an
 * entity but get NO AcDbScale/context OBJECTS → contexts lost on the snapshot
 * export path. Absent / undefined falls through to `space.entities` (legacy).
 *
 * `paperPlan` (F5', round-2) — the #314 paper-space relocation plan. The export
 * emits a relocated LAYOUT block's (`*Model_Space` / first `*Paper_Space` /
 * PAIRED extra `*Paper_Space<N>`) SUPPORTED content via its SPACE copy and leaves
 * the BLOCK def's supported children OUT of the emitted stream. But the importer
 * keeps a COPY of those children on BOTH `space.entities` AND
 * `document.blocks[name].entities`, so scanning BOTH below DOUBLE-COUNTS an
 * annotative entity → duplicate context records / an orphaned owner handle on
 * re-import. To emit exactly ONE context per entity, the blocks scan SKIPS any
 * LAYOUT block whose supported children are emitted via a space (model, first
 * paper, paired extra tab) — its annotative entities are already counted via the
 * space loop. An UNPAIRED suffixed `*Paper_Space<N>` block (F3') is NOT skipped:
 * its content is emitted from the BLOCK def (no paired space), so it must be
 * scanned here. Optional with an undefined default (legacy direct callers that
 * carry no relocated paper spaces — a pure regular-block doc is unaffected).
 */
export function buildAnnotativeObjectsGraph(
  document: DrawingDocument,
  seedHandles: ReadonlySet<string>,
  entitiesSnapshot?: Map<string, DrawingEntity[]>,
  paperPlan?: PaperSpaceLayoutPlan,
): AnnotativeObjectsGraph {
  const acadScales: AcDbScale[] = [];
  const annotScaleContexts: NonNullable<
    DxfObjectsSection["annotScaleContexts"]
  > = [];
  const mintedExtDictHandles = new Map<string, string>();

  // De-dup scale catalogue by name → handle (one AcDbScale per scale name).
  const scaleHandleByName = new Map<string, string>();
  const scaleEntries: Array<{ name: string; handle: string }> = [];

  const mintHandle = makeHandleMinter(seedHandles);

  const ensureScaleHandle = (name: string, scaleFactor: number): string => {
    const existing = scaleHandleByName.get(name);
    if (existing) return existing;
    const handle = mintHandle();
    scaleHandleByName.set(name, handle);
    scaleEntries.push({ name, handle });
    // Reconstruct paper/drawing units so the parser's derived ratio
    // (paperUnits / drawingUnits) collapses back to scaleFactor.
    acadScales.push({
      handle,
      name,
      scaleFactor,
      paperUnits: scaleFactor,
      drawingUnits: 1,
      isUnitScale: scaleFactor === 1,
      flags: 0,
      isHidden: false,
    });
    return handle;
  };

  // Gather every annotative entity from all spaces + block definitions.
  // F2 — read each space's entities from the SAME source the ENTITIES writer
  // uses (`entitiesSnapshot.get(space.id) ?? space.entities`) so a
  // snapshot-only annotative entity still gets its OBJECTS graph emitted.
  const entities: AnnotativeBearingEntity[] = [];
  for (const space of document.spaces ?? []) {
    if (space.kind === "block-test" || space.kind === "block-edit") continue;
    const sourceEntities = entitiesSnapshot?.get(space.id) ?? space.entities;
    for (const entity of sourceEntities) {
      if (hasAnnotativeContexts(entity)) entities.push(entity);
    }
  }
  for (const [name, block] of Object.entries(document.blocks ?? {})) {
    // F5' — SKIP a LAYOUT block whose SUPPORTED children are emitted via a SPACE
    // (so they are already counted by the space loop above). Scanning it too
    // would DOUBLE-COUNT the annotative entity → duplicate context records. This
    // covers `*Model_Space`, the first/active `*Paper_Space` (case-insensitive),
    // and every PAIRED extra `*Paper_Space<N>` tab. An UNPAIRED suffixed
    // `*Paper_Space<N>` block (no paired space — F3') is NOT emitted via a space,
    // so it is NOT skipped here and its annotative children are counted from the
    // block def below (exactly once). Without a `paperPlan` (legacy callers) the
    // emptied LAYOUT block carries no children anyway, so this is a no-op for them.
    if (isLayoutBlockName(name) && isLayoutBlockEmittedViaSpace(name, paperPlan)) {
      continue;
    }
    for (const child of block.entities) {
      if (hasAnnotativeContexts(child as DrawingEntity)) {
        entities.push(child as AnnotativeBearingEntity);
      }
    }
  }

  for (const entity of entities) {
    // Owner-handle anchor: prefer the EMITTED ext-dict handle the writer will
    // actually advertise on this entity. The resolver re-anchors a context to
    // its entity via `ctxRecord.ownerHandle === entity.extensionDictionaryHandle`
    // (handleResolution.ts direct-anchor) — and an INSERT's ext-dict 360 is ONE
    // dictionary that can own BOTH the representation-data entry AND the
    // annotative context-data. So the context owner MUST equal whatever the
    // emitted INSERT advertises, not a freshly minted handle that the export
    // stamp loop would then refuse to overwrite.
    //
    // F1 (Codex #252 P2) — `toDxfEntity` sets the emitted record's
    // `common.extensionDictionaryHandle` to `representationOverrides
    // .representationDataHandle` (a Block-geometry handle) when present, which
    // OVERRIDES the common `extensionDictionaryHandle`. A pure-model annotative
    // INSERT carrying ONLY a representation handle (no common ext-dict) thus
    // emits the representation handle as its 360 — so the context owner must be
    // that representation handle, else the resolver finds no match and drops the
    // contexts. Mirror `toDxfEntity`'s precedence exactly: representation handle
    // wins, then the common ext-dict handle.
    const repDataHandle = (
      entity as {
        representationOverrides?: { representationDataHandle?: string };
      }
    ).representationOverrides?.representationDataHandle;
    const commonExtDict = (entity as { extensionDictionaryHandle?: string })
      .extensionDictionaryHandle;
    // The handle the EMITTED entity record already carries (no stamp needed) —
    // representation handle takes precedence over the common ext-dict, matching
    // `toDxfEntity`'s INSERT branch.
    const preservedExtDict = repDataHandle ?? commonExtDict;
    let ownerHandle =
      preservedExtDict ??
      entity.annotativeContexts.find((c) => c.ownerHandle)?.ownerHandle;
    if (!ownerHandle) {
      ownerHandle = mintHandle();
    }
    // Whenever the entity has NO preserved/emitted ext-dict handle, the chosen
    // anchor (minted OR a context's recorded ownerHandle) MUST be stamped back
    // onto the emitted entity record — otherwise the context records would be
    // orphaned and re-import would yield an empty annotativeContexts list. The
    // preserved/emitted-handle case (common ext-dict OR representation handle)
    // needs no stamp: the writer already emits that 360, so the context owner
    // anchored to it resolves without a further stamp (and the stamp guard
    // would refuse to overwrite an already-present handle anyway).
    if (!preservedExtDict) {
      mintedExtDictHandles.set(entity.id, ownerHandle);
    }

    for (const ctx of entity.annotativeContexts) {
      const scaleHandle = ensureScaleHandle(ctx.scaleName, ctx.scaleFactor);
      annotScaleContexts.push({
        handle: mintHandle(),
        ownerHandle,
        scaleHandle,
        subclass: subclassForKind(ctx.kind),
        // STRUCTURAL-EQUIVALENCE CONTRACT: the model `AnnotScaleContext`
        // (./annotative) and the dxf-io `AnnotScaleContext` MUST stay
        // field-compatible (same `kind` discriminator + identical per-variant
        // field set). The cast relies on that equivalence to keep the
        // single-truth resolver/writer shape without re-deriving fields. If
        // either union gains/renames a variant field, this cast silently
        // diverges — keep the two definitions in lockstep (mirrored by
        // resolveAnnotativeContexts on the inverse/import path). A compile-time
        // assertion is impractical here because the two unions live in separate
        // packages with no shared structural base; the round-trip tests
        // (dxfBridge.insertAnnotative.test.ts / blockChildAnnotative.test.ts)
        // are the regression guard against drift.
        context: ctx as unknown as DxfAnnotScaleContext,
      });
    }
  }

  // F3 (no-drop) — when we emit ANY annotative context, make the AcDbScale
  // catalogue COMPLETE by also folding in every scale from the typed
  // `document.annotationScales` (the authoritative catalogue lifted from the
  // source ACAD_SCALELIST at import — first-wins, non-hidden). Without this the
  // synthesized catalogue would carry ONLY the scales referenced by an entity
  // context; a scale that exists in the source SCALELIST but is currently
  // unreferenced by any annotative entity would be DROPPED when we replace the
  // preserved raw SCALELIST with the synthesized one. We seed only on the
  // graph-emitted path (≥1 context) so a non-annotative document never gains a
  // spurious SCALELIST. (Hidden scales are already absent from the typed model
  // pre-F3 — and their AcDbScale records were never re-injected raw on import —
  // so they are out of scope here, not a regression of this fix.)
  if (annotScaleContexts.length > 0) {
    for (const scale of document.annotationScales ?? []) {
      ensureScaleHandle(scale.name, scale.scaleFactor);
    }
  }

  const graph: AnnotativeObjectsGraph = {
    acadScales,
    annotScaleContexts,
    mintedExtDictHandles,
  };
  if (scaleEntries.length > 0) {
    // F3 (single-source) — REUSE a preserved ACAD_SCALELIST dictionary's handle
    // when one survived import in `document.rawObjectGroups`, instead of minting
    // a second. The caller strips the now-stale preserved SCALELIST container
    // record from `rawObjectGroups` and re-emits THIS synthesized dict under the
    // SAME handle — so the root named-object dictionary's existing
    // `3/ACAD_SCALELIST` + `350/<handle>` pointer still resolves to exactly ONE
    // SCALELIST container, whose entries point at the (only) emitted AcDbScale
    // records. No preserved dict → mint a fresh handle as before.
    const preservedScaleListHandle = findPreservedScaleListHandle(
      document.rawObjectGroups,
    );
    const scaleListHandle = preservedScaleListHandle ?? mintHandle();
    const acadScaleListDict: AcadScaleListDictEntry = {
      handle: scaleListHandle,
      scales: scaleEntries,
    };
    // SL-16 P3 — the SCALELIST DICTIONARY must carry its `330` owner (root
    // NamedObjects dict) or ezdxf `audit()` flags it orphaned
    // (`INVALID_OWNER_HANDLE`). On the REUSE path (a preserved SCALELIST
    // container survived in `rawObjectGroups`) source-preserve its own owner +
    // `102 {ACAD_REACTORS}` block verbatim; on the fresh-synth path fall back to
    // the root NamedObjects dict handle (the record carrying `3/ACAD_SCALELIST`,
    // present in `rawObjectGroups` on both the imported + conformant-skeleton
    // export paths).
    const preservedMeta = preservedScaleListHandle
      ? extractPreservedScaleListDictMeta(
          document.rawObjectGroups,
          preservedScaleListHandle,
        )
      : {};
    const ownerHandle =
      preservedMeta.ownerHandle ??
      findNamedObjectsRootHandle(document.rawObjectGroups);
    if (ownerHandle) acadScaleListDict.ownerHandle = ownerHandle;
    if (preservedMeta.reactorHandles) {
      acadScaleListDict.reactorHandles = preservedMeta.reactorHandles;
    }
    graph.acadScaleListDict = acadScaleListDict;
    if (preservedScaleListHandle) {
      graph.reusedScaleListHandle = preservedScaleListHandle;
    }
    // F2 (Codex #252 P2) — anchor every synthesized AcDbScale record back at the
    // ACAD_SCALELIST dictionary via group 330 (`ownerHandle`). The section
    // parser (sectionParsers.ts) reconstructs the typed `acadScaleListDict` by
    // COUNTING AcDbScale.ownerHandle values and picking the most-common one as
    // the SCALELIST dict handle, then re-locating that dict in `dictionaries[]`.
    // Without an ownerHandle the re-parsed file carries `acadScales` but NO typed
    // `acadScaleListDict` → typed consumers lose the scale-list-dictionary
    // projection. The dict handle is only known here (minted or reused), so we
    // patch the records after it is chosen. The emitted SCALELIST DICTIONARY
    // (serializeAcDbScaleListDict) lands in `dictionaries[]` under this same
    // handle, so the parser's re-location step succeeds.
    for (const scale of acadScales) {
      scale.ownerHandle = scaleListHandle;
    }
  }
  return graph;
}
